import type { ReactElement } from 'react'
import { Box, ListItem, ListItemButton, Tooltip, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import type { UserNotification } from '../../api/contracts'
import { formatDateTime } from '../../utils/formatters'
import { getNotificationKind } from './notificationKinds'
import { formatNotificationTime } from './notificationTime'
import { NOTIFICATIONS_TEXT } from './notificationsText'

interface NotificationListItemProps {
  notification: UserNotification
  now: Date
  /** Without a handler the row has no link and is not a button. */
  onClick?: () => void
}

const UNREAD_BACKGROUND = '#f5f8ff'

const rowSx = {
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '32px minmax(0, 1fr) auto',
  gap: '12px',
  alignItems: 'start',
  width: '100%',
  padding: '12px 16px',
  textAlign: 'left'
} as const

/** Feed row: kind icon, title and one-line body as plain text, time; unread rows get a dot and a light background. */
export default function NotificationListItem({ notification, now, onClick }: NotificationListItemProps): ReactElement {
  const kind = getNotificationKind(notification.eventType)
  const isUnread = notification.readAtUtc === null
  const Icon = kind.Icon
  const background = isUnread ? UNREAD_BACKGROUND : 'transparent'

  const content = (
    <>
      {isUnread && (
        <Box
          component="span"
          aria-hidden="true"
          data-testid="notification-unread-dot"
          sx={{ position: 'absolute', left: 6, top: 20, width: 6, height: 6, borderRadius: '50%', bgcolor: '#b42318' }}
        />
      )}
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: kind.background,
          color: kind.color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
        {isUnread && <Box component="span" sx={visuallyHidden}>{NOTIFICATIONS_TEXT.unreadMarker}</Box>}
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: 13.5, lineHeight: 1.4, color: '#15202b', overflowWrap: 'anywhere' }}
        >
          {notification.title}
        </Typography>
        {notification.body && (
          <Tooltip title={notification.body}>
            <Typography
              component="span"
              noWrap
              sx={{ display: 'block', fontSize: 12.5, lineHeight: 1.45, color: '#6b7482', mt: '2px' }}
            >
              {notification.body}
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Typography
        component="time"
        dateTime={notification.createdAtUtc}
        title={formatDateTime(notification.createdAtUtc)}
        sx={{ fontSize: 12, lineHeight: 1.45, color: '#6b7482', whiteSpace: 'nowrap', pt: '1px', fontVariantNumeric: 'tabular-nums' }}
      >
        {formatNotificationTime(notification.createdAtUtc, now)}
      </Typography>
    </>
  )

  return (
    <ListItem disablePadding sx={{ display: 'block', borderTop: '1px solid #e6eaf1', '&:first-of-type': { borderTop: 0 } }}>
      {onClick ? (
        <ListItemButton
          onClick={onClick}
          sx={{
            ...rowSx,
            bgcolor: background,
            '&:hover': { bgcolor: isUnread ? '#edf2fd' : 'action.hover' }
          }}
        >
          {content}
        </ListItemButton>
      ) : (
        <Box sx={{ ...rowSx, bgcolor: background }}>{content}</Box>
      )}
    </ListItem>
  )
}
